
import React from 'react';
import { get, has, keyBy, filter, map, includes, toUpper } from 'lodash';
import styled from 'styled-components';
import { Switch } from 'antd';
import {
    Text,
    StatusTag,
    Number,
    FlexContainer,
    GetUnitPrice,
    Icon,
    Image,
    ConditionalRender,
    If,
    AppIcon, Column
} from '../../../../../../components/common';
import { formatRefId, productPickingType } from '../../../../../../utils/misc';
import { generateColumns } from './AuditColumnsGenerator';
import { substituteColumns, excessColumns } from './AuditTableColumns';
import './audit_switch.css';

const TagBase = styled.div`
    height: 20px;
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    display: inline-block;
    background: #166DC2;
    border: 1px solid #166DC2;
`;

const ClickableLink = styled.a`
    color: black;
`;

const _renderCell = (item, dataIndex) => (get(item, dataIndex, '-'));

const parentColumns = [
    {
        title: 'Imagen',
        dataIndex: 'imageUrl',
        key: 'imageUrl',
        index: 1,
        render: (item, dataindex) => {
            return <Image src={get(item, 'imageUrl')} />;
        }
    },
    {
        title: 'Producto',
        dataIndex: 'name',
        width: 150,
        key: 'product',
        index: 2,
        render: (item, dataIndex, { onRowClick = () => (null) }) => (
            <div>
                <ClickableLink onClick={(e) => { e.preventDefault(); onRowClick(item); }}>
                    <p>{get(item, dataIndex)}</p>
                </ClickableLink>
                <FlexContainer mY={5} align='center'>
                    <Icon name='barcode' color='#666666' size={15} right={5} />
                    <Text text={get(item, 'ean', 'No Disponible')} color='#666666' size={11} lineHeight={1} />
                </FlexContainer>
                <ConditionalRender condition={has(item, 'priceTags.0.rateAndBenefit.name')}>
                    <div style={{ display: 'flex' }}>
                        <AppIcon name='ic-descuento' fill='#666666' style={{ marginRight: '3px', fontSize: '11px' }} />
                        <Text text={get(item, 'priceTags.0.rateAndBenefit.name')} tiny color='#666666' />
                    </div>
                </ConditionalRender>
            </div>
        )
    },
    {
        title: 'Marca',
        dataIndex: 'brandName',
        key: 'brandName',
        index: 3,
        columnStyle: { textAlign: 'left' },
        render: (item, dataIndex) => <Text text={get(item, dataIndex, '')} size={11} />
    },
    {
        title: 'Solicitado',
        dataIndex: 'quantity',
        key: 'quantity',
        index: 4,
        render: (item, dataIndex) => {
            const quantity = get(item, dataIndex, '0');
            const unitMultiplier = get(item, 'unitMultiplier', '0');
            if (String(quantity) === '0') return _renderCell(null, null);
            // return `${(String(quantity * parseFloat(unitMultiplier))).replace('.', ',')} ${get(item, 'unit', 'un')}`;
            const calculatedQuantity = quantity * parseFloat(unitMultiplier);
            const measureUnit = get(item, 'unit', 'un');
            const valueToShow = measureUnit === 'kg' ? parseFloat(calculatedQuantity).toFixed(3) : calculatedQuantity;
            return `${String(valueToShow).replace('.', ',')} ${measureUnit}`;
        }
    },
    {
        title: 'Resultado picking',
        dataIndex: 'picking.pickingQuantity',
        index: 5,
        key: 'picked',
        render: (item, dataIndex) => {
            const status = get(item, 'picking.status');
            const shouldHide = ['FREE_PICKING_ACCEPTED', 'SUBSTITUTE_CONFIRMED'];
            const quantity = get(item, dataIndex, '0');
            const measureUnit = get(item, 'unit', 'un');
            const value = get(item, dataIndex);
            const valueToShow = measureUnit === 'kg' ? parseFloat(value).toFixed(3) : value;
            if (String(quantity) === '0' || shouldHide.includes(status)) return _renderCell(null, null);
            return `${(String(valueToShow).replace('.', ','))} ${measureUnit}`;
        }
    },
    {
        title: 'Stock Disponible',
        dataIndex: 'stock',
        index: 6,
        key: 'picked',
        render: (item, dataIndex, ctx) => {
            const stockMap = get(ctx, 'stockMap', {});
            const stock = parseInt(get(stockMap, `${formatRefId(get(item, 'refId', ''))}.stock`));
            return <Text size={11} text={`${stock || '0'} ${toUpper(get(item, 'unit', 'un'))}`} strong />;
        }
    },
    {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        index: 7,
        render: (item, dataIndex) => {
            const status = productPickingType(get(item, 'picking.status', get(item, 'status')));
            return (
                <FlexContainer>
                    <StatusTag mL={0} color='#1fa02e' key={dataIndex} status={`order_detail.item.picking.status.${status}`} tagType={status} />
                </FlexContainer>
            );
        }
    },
    {
        title: 'Regla',
        dataIndex: 'picking.productId.info',
        key: 'audit-rules',
        index: 8,
        render: (item, dataIndex, { auditInfo }) => {
            const productId = get(item, 'picking.id');
            const auditResults = get(keyBy(get(auditInfo, 'results.failed'), 'productId'), productId);
            const filteredByDecision = filter(get(auditResults, 'audits'), ({ decision }) => decision);
            return (
                <FlexContainer direction='column'>
                    {map(filteredByDecision, ({ value, codeRule }) => (
                        <StatusTag mL={0} key={dataIndex} status={`order_audit.status.${codeRule}`} tagType='error' />
                    ))}
                </FlexContainer>
            );
        }
    },
    {
        title: 'Umbral',
        dataIndex: 'picking.productId.umbral',
        key: 'error-umbral',
        index: 9,
        columnStyle: { textAlign: 'right' },
        render: (item, dataIndex, { auditInfo }) => {
            const productId = get(item, 'picking.id');
            const auditResults = get(keyBy(get(auditInfo, 'results.failed'), 'productId'), productId);
            const filteredByDecision = filter(get(auditResults, 'audits'), ({ decision }) => decision);
            return (
                <FlexContainer direction='column'>
                    {map(filteredByDecision, ({ value, codeRule }) => {
                        if (codeRule !== '8') {
                            return (
                                <Text lineHeight={2.5} size={11} text={`${parseInt(value)} %`} />
                            );
                        }
                        return <FlexContainer margin={19} />;
                    })}
                </FlexContainer>
            );
        }
    },
    {
        title: 'Precio unitario',
        dataIndex: 'basePrice',
        key: 'client',
        index: 10,
        columnStyle: { textAlign: 'right' },
        render: item => (
            <GetUnitPrice item={item}>
                {({ priceTags, listPrice, price }) => (
                    <>
                        {priceTags && (
                            <div>
                                <span>
                                    <del style={{ color: '#666666' }}><Number value={listPrice} /></del>
                                </span>
                            </div>
                        )}
                        <div>
                            <span>
                                <Number value={price} />
                            </span>
                        </div>
                    </>
                )}
            </GetUnitPrice>
        )
    },
    {
        title: 'Total solicitado',
        dataIndex: 'sellingPrice',
        index: 11,
        key: 'sale_price',
        render: (item, dataIndex) => {
            const { quantity = 0 } = item;
            return (
                <div>
                    <span>
                        <Number value={get(item, dataIndex) * parseFloat(quantity)} />
                    </span>
                </div>
            );
        }
    },
    {
        title: 'Total pickeado',
        dataIndex: 'picking',
        key: 'final_price',
        index: 12,
        render: (item, dataIndex) => {
            const data = get(item, dataIndex, {});
            const { sellingPrice, pickingQuantity, unitMultiplier } = data;
            const value = parseInt(sellingPrice) * (parseFloat(pickingQuantity) / parseFloat(unitMultiplier));
            return (
                <div>
                    <strong>
                        { value > 0 ? <Number value={value} /> : '-' }
                    </strong>
                </div>
            );
        }
    },
    {
        title: 'Validación',
        dataIndex: 'picking.productId.validate',
        key: 'validate-item',
        index: 13,
        render: (item, dataIndex, ctx) => {
            const id = get(item, 'picking.productId', get(item, 'productId'));
            const refId = formatRefId(get(item, 'refId', ''));
            const { formIsSubmitted, formValues = {}, onChangeValue, stockMap = {} } = ctx;
            const switchOptions = [{ value: 1, label: 'Aprobar' }, { value: 0, label: 'Rechazar' }];
            const isSplit = get(item, 'picking.split');
            const stock = parseInt(get(stockMap, `${refId}.stock`));

            const defaultRejected = (stock, hasHandler) => {
                if (isSplit) {
                    return false
                } else {
                    if (includes(['PARTIAL', 'PARTIAL_ACCEPTED', 'OUT_STOCK', 'OUT_STOCK_ACCEPTED'], get(item, 'picking.status'))) {
                        const originalQuantity = parseFloat(get(item, 'quantity', '0')) * parseFloat(get(item, 'unitMultiplier', '0'));
                        const missingQuantity = originalQuantity - parseFloat(get(item, 'picking.pickingQuantity'));
                        if ((missingQuantity > 0) && (parseFloat(stock) > missingQuantity)) {
                            if (hasHandler) return onChangeValue({ name: id, value: switchOptions[1] });
                            return true;
                        }
                    }
                }
            };

            return (
                <If
                    condition={formIsSubmitted}
                    then={(
                        <FlexContainer direction='column'>
                            <Text text={get(formValues, `${id}`, true) ? 'Aprobado' : 'Rechazado'} />
                        </FlexContainer>
                    )}
                    else={(
                        <Column height='100%' jsutify='center' gap={10}>
                            {isSplit && <TagBase>
                                <FlexContainer align='center' height='100%'>
                                    Split Order
                                </FlexContainer>
                            </TagBase>}
                            <Switch
                                defaultChecked={!defaultRejected(stock)}
                                checkedChildren='Aprobar'
                                unCheckedChildren='Rechazar'
                                name={id}
                                onChange={(value) => {
                                    if (value) return onChangeValue({ name: id, value: switchOptions[0] });
                                    return onChangeValue({ name: id, value: switchOptions[1] });
                                }}
                            />
                        </Column>
                    )}
                />
            );
        }
    }
];

export const getColumns = () => {
    const columns = generateColumns([
        {
            key: 'parentColumns',
            scheme: [1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
            columnSet: parentColumns
        },
        {
            key: 'excessColumns',
            scheme: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1],
            columnSet: excessColumns
        },
        {
            key: 'substituteColumns',
            scheme: [4, 1, 8, 3, 0, 0, 0, 0, 5, 8, 7],
            columnSet: substituteColumns
        }
    ]);

    return columns;
};
